import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { DatePicker, PrimaryButton } from 'office-ui-fabric-react';
import { DateCustom } from '../../../lib/DateCustom';
import MessageCourier from '../../../lib/MessageCourier';
import TimeTaxSupportSystemClient from '../../../system';
import { useTranslation } from 'react-i18next';
import { getLocalizedStrings } from '../../../lib/LocalizedStringsForDatePicker';
import { User } from '../../../system/User';
import DataGrid, { Scrolling, Column, Summary, Export, SearchPanel, TotalItem, GroupItem, Paging } from 'devextreme-react/data-grid';
import { IReporteGralHoras } from '../../../system/reporting/ReportingSystem';
import { crearSelectorColores } from '../../../lib/crearSelectorColores';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { ExportingEvent } from 'devextreme/ui/data_grid';
import { getCorporativoInfo, getDataBase64 } from '../../../lib/Util';
import { APP_NAME } from '../../../common/constants';
import { exportDataGrid } from 'devextreme/excel_exporter';
import ReactECharts from 'echarts-for-react';
import { IorganizationLevels } from '../../../system/AuthenticationSystem';

interface IAvancePorSectorProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (change: boolean) => void;
  user: User;
}

type ChartDataItem = {
  name: string;
  value: number;
  color: string;
};

const datetime = DateTime.now();
const today = datetime.toJSDate();
const firstDayOfMonth = DateTime.fromJSDate(today).startOf('month').toJSDate();
const lastDayOfMonth = DateTime.fromJSDate(today).endOf('month').toJSDate();

const AvancePorSector = (props: IAvancePorSectorProps) => {
  const { t } = useTranslation();

  const [fromDate, setFromDate] = useState<DateTime>(DateTime.fromJSDate(firstDayOfMonth));
  const [toDate, setToDate] = useState<DateTime>(DateTime.fromJSDate(lastDayOfMonth));
  const [items, setItems] = useState<IReporteGralHoras[]>([]);
  const [chartData1, setChartData1] = useState<ChartDataItem[]>([]);
  const [chartData2, setChartData2] = useState<ChartDataItem[]>([]);
  const [levelsN, setLevelsN] = useState<IorganizationLevels[]>([]);

  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<HTMLDivElement>(null);
  const gridRef = useRef<DataGrid>(null);

  /* ============================
       CARGA NIVELES
    ============================ */
  useEffect(() => {
    const loadLevels = async () => {
      const data = await props.system.getOrganizationLevels();
      setLevelsN(data);
    };
    loadLevels();
  }, []);

  const buildJerarquia = (data: any[]) => {
    const tree: any = {};

    const ultimoNivelMaster = levelsN[levelsN.length - 1];
    const nivelPadreMaster = levelsN[0];

    data.forEach((item) => {
      const path = item.organigrama_path_nombre;
      const esUltimoNivel = item.ultimo_nivel;

      const ultimoNivel = item[ultimoNivelMaster.nio_nombre_tec];

      let current = tree;

      // Agregar primero el nodo padre del item
      const nodoPadre = item[nivelPadreMaster.nio_nombre_tec] || 'SIN ESTRUCTURA';
      if (!current[nodoPadre]) {
        current[nodoPadre] = {
          nombre: nodoPadre,
          hijos: {},
          items: [],
        };
      }

      // Luego agregar el nodo del último nivel, pero solo si es necesario para mantener la estructura aunque no tenga items asignados
      if ((esUltimoNivel || path) && ultimoNivel) {
        if (!current[nodoPadre].hijos[ultimoNivel]) {
          current[nodoPadre].hijos[ultimoNivel] = {
            nombre: ultimoNivel,
            hijos: {},
            items: [],
          };
        }
      }

      // Agregar el item al nodo del último nivel si es que es el último nivel o si no tiene path pero tiene un nivel identificable
      if (esUltimoNivel) {
        current[nodoPadre].hijos[ultimoNivel].items.push(item);
      } else if (!esUltimoNivel) {
        // Si el item no es del último nivel, pero tampoco tiene path, lo asigno a un nodo genérico dentro de su nodo padre para no perderlo y mostrar que no completó la estructura
        const key = 'no_completo_estructura';
        if (!current[nodoPadre].hijos[key]) {
          current[nodoPadre].hijos[key] = {
            nombre: `Area vacía (${nodoPadre.toLowerCase()})`,
            hijos: {},
            items: [],
          };
        }
        current[nodoPadre].hijos[key].items.push(item);
      }
    });

    return tree;
  };

  const flattenTree = (node: any, nivel = 0, parent = null, root = null) => {
    let result: any[] = [];

    Object.values(node).forEach((nodo: any) => {
      // si estoy en nivel 0, yo soy el root
      const currentRoot = nivel === 0 ? nodo.nombre : root;

      // hijos primero
      result = result.concat(flattenTree(nodo.hijos, nivel + 1, nodo.nombre, currentRoot));

      // cálculos
      const colaboradores = nodo.items.length;

      // sumo horas de todos los colaboradores de ese nodo
      const horas = nodo.items.reduce((acc: number, i: any) => acc + Number(i.horas || 0), 0);

      // colaboradores que completaron su carga horaria esperada
      const completaron = nodo.items.filter((i: any) => i.porcentaje_carga >= 100).length;

      // colaboradores que no completaron su carga horaria esperada
      const noCompletaron = colaboradores - completaron;

      // Completaron / Total de colaboradores del nodo, pero solo si el nodo tiene colaboradores, si no, el porcentaje es 0 para evitar división por 0
      const porcentaje = colaboradores > 0 ? Math.ceil((completaron / colaboradores) * 10000) / 100 : 0; // redondeo a 2 decimales

      result.push({
        nombre: nodo.nombre,
        colaboradores,
        horas,
        completaron,
        noCompletaron,
        porcentaje,
        nivel,
        parent,
        isParent: nivel === 0,
        grupo: currentRoot,
      });
    });

    return result;
  };

  const buildJerarquiaDinamica = (data: any[]) => {
    const tree = buildJerarquia(data);
    return flattenTree(tree);
  };

  const resumenDataGrid = buildJerarquiaDinamica(items).filter((x) => !x.isParent);

  const onChangeDate = (date: Date | null | undefined, name: string) => {
    if (date) {
      if (name === 'from') {
        // Primer dia del mes seleccionado
        const firstDay = DateTime.fromJSDate(date).startOf('month');
        setFromDate(firstDay);

        // Ultimo dia del mes seleccionado
        const lastDay = DateTime.fromJSDate(date).endOf('month');
        setToDate(lastDay);
      }
    }
  };

  const handleSearch = async () => {
    props.changeBlockUI(true);
    try {
      if (fromDate && toDate) {
        const _items = await props.system.getReportingSystem().getGralHorasReport(fromDate, toDate);
        setItems(_items);

        const resumen = buildJerarquiaDinamica(_items);

        const padres = resumen.filter((x) => x.isParent);
        const hijos = resumen.filter((x) => !x.isParent);

        const selectorPadres = crearSelectorColores(padres.length);
        const selectorHijos = crearSelectorColores(hijos.length);

        const chartData1: ChartDataItem[] = padres.map((padre) => {
          const hijosDelPadre = hijos.filter((hijo) => hijo.grupo === padre.nombre);

          // promedio de sus procentajes
          const porcentaje =
            Math.ceil(((hijosDelPadre.reduce((acc, hijo) => acc + hijo.porcentaje, 0) + padre.porcentaje) / hijosDelPadre.length) * 100) / 100;

          return {
            name: padre.nombre,
            value: porcentaje,
            color: selectorPadres.obtenerColor(),
          };
        });

        const chartData2: ChartDataItem[] = hijos.map((item) => {
          return {
            name: item.nombre,
            value: item.porcentaje,
            color: selectorHijos.obtenerColor(),
          };
        });

        setChartData1(chartData1);
        setChartData2(chartData2);
        props.courier.messageSuccess(t('Reporte generado con éxito'));
      } else {
        props.courier.messageError(t('Debes seleccionar un sector'));
      }
    } catch (error) {
      props.courier.messageError(error);
    }
    props.changeBlockUI(false);
  };

  const exportDataGridToExcel = async (e: ExportingEvent) => {
    e.cancel = true;
    const corporativoInfo = getCorporativoInfo();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Reporte');

    // =========================
    // HEADER PERSONALIZADO
    // =========================
    const imageId = workbook.addImage({
      base64: getDataBase64(),
      extension: 'png',
    });

    worksheet.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 300, height: 80 },
    });

    const firstrow = worksheet.getRow(1);
    firstrow.height = 80; // Altura específica para las filas de datos
    firstrow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };

    // header  con nombre de la app
    const headerRow = worksheet.getRow(2);
    headerRow.getCell(1).value = APP_NAME;

    // titulo del reporte
    const titleRow = worksheet.getRow(3);
    worksheet.mergeCells(3, 1, 3, 8);

    titleRow.height = 30;
    titleRow.getCell(1).value = 'Reporte de avance por sector';
    titleRow.getCell(1).font = { name: 'Segoe UI Light', size: 22 };
    titleRow.getCell(1).alignment = { horizontal: 'center' };

    // Agregar info extra debajo del título
    worksheet.getRow(4).getCell(1).value = 'Periodo:';
    worksheet.getRow(4).getCell(1).font = { bold: true };
    worksheet.getRow(4).getCell(2).value = fromDate ? fromDate.toFormat('yyyy-LL') : '';

    worksheet.getRow(5).getCell(1).value = 'Corporativo:';
    worksheet.getRow(5).getCell(1).font = { bold: true };
    worksheet.getRow(5).getCell(2).value = corporativoInfo ? corporativoInfo.empp_nombre : '';

    // =========================
    // EXPORTAR GRID
    // =========================
    await exportDataGrid({
      component: e.component,
      worksheet,
      topLeftCell: { row: 7, column: 1 }, // empieza debajo del header
      customizeCell: ({ gridCell, excelCell }) => {
        if (!gridCell || !excelCell || !gridCell.column) return;

        excelCell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };

        // estilos para filas de grupo
        if (gridCell.rowType === 'group') {
          excelCell.font = { bold: true };
          excelCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFEFEFEF' },
          };
        }
      },
    });

    // =========================
    // GRÁFICOS
    // =========================
    const canvas1 = await html2canvas(chartRef1.current!);
    const canvas2 = await html2canvas(chartRef2.current!);

    const img1 = workbook.addImage({
      base64: canvas1.toDataURL(),
      extension: 'png',
    });

    const img2 = workbook.addImage({
      base64: canvas2.toDataURL(),
      extension: 'png',
    });

    const lastRow = worksheet.lastRow?.number || 10;

    worksheet.addImage(img1, {
      tl: { col: 0, row: lastRow + 2 },
      ext: { width: chartRef1.current?.offsetWidth || 400, height: chartRef1.current?.offsetHeight || 500 },
    });

    worksheet.addImage(img2, {
      tl: { col: 4, row: lastRow + 2 },
      ext: { width: chartRef2.current?.offsetWidth || 400, height: chartRef2.current?.offsetHeight || 500 },
    });

    // =========================
    // DESCARGA
    // =========================
    const buffer = await workbook.xlsx.writeBuffer();
    const periodo = fromDate ? fromDate.toFormat('yyyyLL') : 'periodo';
    saveAs(new Blob([buffer]), `avance_por_sector_${periodo}.xlsx`);
  };

  return (
    <div>
      <div className="ms-Grid-row mt-1">
        <div className="ms-Grid-col ms-sm3 ">
          <DatePicker
            strings={getLocalizedStrings()}
            label={t('Periodo')}
            formatDate={DateCustom.formatDateMMYYYY}
            value={fromDate ? fromDate.toJSDate() : undefined}
            onSelectDate={(e) => onChangeDate(e, 'from')}
            calendarProps={{
              showGoToToday: false,
              isDayPickerVisible: false,
              strings: null,
            }}
          />
        </div>
        <div className="ms-Grid-col ms-sm3 ">
          <PrimaryButton iconProps={{ iconName: 'Search' }} className="mt-for-btn-search ml-2" onClick={() => handleSearch()} text={t('Buscar')} />
        </div>
      </div>
      <hr />

      {resumenDataGrid.length > 0 && (
        <>
          <div style={{ width: '100%', overflow: 'auto' }}>
            <DataGrid
              ref={gridRef}
              dataSource={resumenDataGrid}
              showBorders={true}
              columnAutoWidth={true}
              width={'100%'}
              onExporting={exportDataGridToExcel}
            >
              <SearchPanel visible={true} />
              <Export enabled={true} />
              <Scrolling mode="standard" />
              <Paging enabled={false} />

              <Column
                dataField="grupo"
                groupCellRender={(params) => {
                  return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
                }}
                groupIndex={0}
                visible={false}
              />

              <Column dataField="nombre" caption={t('Área')} />

              <Column caption={t('Cumplimiento general por sector')} alignment="center">
                <Column dataField="colaboradores" caption={t('# Colaboradores')} />
                <Column dataField="horas" caption={t('Horas Confirmadas')} />
                <Column dataField="completaron" caption={t('# Completaron registro de horas')} />
                <Column dataField="noCompletaron" caption={t('# No completaron registro de horas')} />
                <Column dataField="porcentaje" caption={t('% de cumplimiento')} cellRender={(e) => <span>{e.value}%</span>} />
              </Column>

              <Summary>
                {/* TOTALES POR GRUPO */}
                <GroupItem column="colaboradores" summaryType="sum" displayFormat="{0}" alignByColumn={true} />
                <GroupItem
                  column="horas"
                  summaryType="sum"
                  displayFormat="{0}"
                  alignByColumn={true}
                  valueFormat={{ type: 'fixedPoint', precision: 2 }}
                />
                <GroupItem column="completaron" summaryType="sum" displayFormat="{0}" alignByColumn={true} />
                <GroupItem column="noCompletaron" summaryType="sum" displayFormat="{0}" alignByColumn={true} />
                <GroupItem
                  column="porcentaje"
                  summaryType="avg"
                  displayFormat="{0}%"
                  alignByColumn={true}
                  customizeText={(e) => Math.ceil(e.value * 100) / 100 + '%'}
                  valueFormat={{ type: 'fixedPoint', precision: 2 }}
                />

                {/* TOTAL GENERAL */}
                <TotalItem column="nombre" summaryType="count" displayFormat={t('TOTAL')} />
                <TotalItem column="colaboradores" summaryType="sum" displayFormat="{0}" />
                <TotalItem column="horas" summaryType="sum" displayFormat="{0}" valueFormat={{ type: 'fixedPoint', precision: 2 }} />
                <TotalItem column="completaron" summaryType="sum" displayFormat="{0}" />
                <TotalItem column="noCompletaron" summaryType="sum" displayFormat="{0}" />
                {/* <TotalItem column="porcentaje" summaryType="sum" displayFormat="{0}%" valueFormat={{ type: 'fixedPoint', precision: 2 }} /> */}
              </Summary>
            </DataGrid>
          </div>

          {chartData1.length > 0 && chartData2.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                margin: '80px auto',
              }}
            >
              <div ref={chartRef1} style={{ height: 400 }}>
                <ReactECharts
                  style={{ height: '100%', width: '100%' }}
                  opts={{ devicePixelRatio: window.devicePixelRatio }}
                  option={{
                    title: {
                      text: t('Cumplimiento de avance por área'),
                      subtext: t('Porcentaje (%)'),
                      left: 'center',
                      textStyle: {
                        fontSize: 16,
                        fontWeight: 'bold',
                      },
                      subtextStyle: {
                        fontSize: 12,
                        color: '#666',
                      },
                    },
                    tooltip: {
                      trigger: 'axis', // muestro tooltip al pasar por cualquier parte de la barra
                      formatter: (params) => `${params[0].value}%`, // solo muestro el valor con % en el tooltip
                    },
                    grid: {
                      // más espacio abajo porque los labels son largos
                      left: 40,
                      right: 20,
                      bottom: 160,
                      top: 100, // más espacio arriba para el título
                    },
                    xAxis: {
                      // muestro todos los labels, aunque se encimen, para que se vea el detalle de cada área
                      type: 'category',
                      data: chartData1.map((i) => i.name),
                      axisLabel: {
                        interval: 0,
                        rotate: 30,
                      },
                    },
                    yAxis: {
                      type: 'value',
                      axisLabel: {
                        formatter: '{value}%',
                      },
                    },
                    dataZoom: [
                      {
                        type: 'inside',
                      },
                    ],
                    series: [
                      {
                        type: 'bar',
                        data: chartData1.map((i) => ({
                          value: i.value,
                          itemStyle: {
                            color: i.color,
                          },
                        })),
                        label: {
                          show: true, // activa labels
                          position: 'top', // arriba de la barra
                          formatter: '{c}%', // agrega %
                        },
                      },
                    ],
                  }}
                />
              </div>
              <div ref={chartRef2} style={{ height: 400 }}>
                <ReactECharts
                  style={{ height: '100%', width: '100%' }}
                  opts={{ devicePixelRatio: window.devicePixelRatio }}
                  option={{
                    title: {
                      text: t('Cumplimiento de avance por sector'),
                      subtext: t('Porcentaje (%)'),
                      left: 'center',
                      textStyle: {
                        fontSize: 16,
                        fontWeight: 'bold',
                      },
                      subtextStyle: {
                        fontSize: 12,
                        color: '#666',
                      },
                    },
                    tooltip: {
                      trigger: 'axis',
                      formatter: (params) => `${params[0].value}%`,
                    },
                    grid: {
                      left: 40,
                      right: 20,
                      bottom: 160,
                      top: 100, // más espacio arriba para el título
                    },
                    xAxis: {
                      type: 'category',
                      data: chartData2.map((i) => i.name),
                      axisLabel: {
                        interval: 0, // muestra TODOS
                        rotate: 30, // evita que se encimen
                      },
                    },
                    yAxis: {
                      type: 'value',
                      axisLabel: {
                        formatter: '{value}%',
                      },
                    },
                    dataZoom: [
                      {
                        type: 'inside', // scroll con mouse
                      },
                    ],
                    series: [
                      {
                        type: 'bar',
                        data: chartData2.map((i) => ({
                          value: i.value,
                          itemStyle: {
                            color: i.color,
                          },
                        })),
                        label: {
                          show: true, // activa labels
                          position: 'top', // arriba de la barra
                          formatter: '{c}%', // agrega %
                        },
                      },
                    ],
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AvancePorSector;
